/**
 * Payment Context — PAYG prepaid balance (ADR 0002).
 *
 * App-wide balance state. Server-derived (balance comes from Stripe top-up
 * webhook → users.balance_cents), like AuthContext (server truth, client cache).
 * Mounted in AppShell, authed-only, inside I18nProvider.
 *
 * - fetches /status on mount
 * - `refresh()` re-reads (post-top-up reconciliation, manual)
 * - `debit(n)` optimistic post-generate balance bump — no refetch
 *
 * Fetch failures are swallowed: the /generate 402 gate is the real backstop,
 * not client state. Balance must never be lied about optimistically above the
 * server's atomic debit.
 */

import { createContext, useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { getPaymentStatus, type PaymentStatus } from '@/lib/payment-client'

export interface PaymentState {
  status: PaymentStatus | null
  isLoading: boolean
  /** Can the user afford one card-gen right now? (balance ≥ cost) */
  canGenerate: boolean
  /** Re-read /status; returns the fetched status (or null on failure). */
  refresh: () => Promise<PaymentStatus | null>
  /** Optimistically decrement balance after a successful generate (no refetch). */
  debit: (cents: number) => void
}

// eslint-disable-next-line react-refresh/only-export-components
export const PaymentContext = createContext<PaymentState>({
  status: null,
  isLoading: true,
  canGenerate: false,
  refresh: async () => null,
  debit: () => {},
})

export function PaymentProvider({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = useState<PaymentStatus | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  // Mounted guard: getPaymentStatus() can resolve after the provider unmounts
  // (test teardown, fast nav). React 19's setState scheduling reads `window`
  // (resolveUpdatePriority), so a late unguarded setState crashes torn-down
  // jsdom with "window is not defined" and leaks an unhandled rejection.
  const mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  const refresh = useCallback(async (): Promise<PaymentStatus | null> => {
    try {
      const next = await getPaymentStatus()
      if (mounted.current) setStatus(next)
      return next
    } catch {
      // Silent — /generate 402 gate backstops balance server-side.
      return null
    } finally {
      if (mounted.current) setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    refresh()
  }, [refresh])

  const debit = useCallback((cents: number) => {
    setStatus((prev) =>
      prev ? { ...prev, balanceCents: Math.max(0, prev.balanceCents - cents) } : prev
    )
  }, [])

  const canGenerate = !!status && status.balanceCents >= status.costCents

  const value = useMemo<PaymentState>(
    () => ({
      status,
      isLoading,
      canGenerate,
      refresh,
      debit,
    }),
    [status, isLoading, canGenerate, refresh, debit]
  )

  return (
    <PaymentContext.Provider value={value}>{children}</PaymentContext.Provider>
  )
}
